import React from 'react'
import { ApolloProvider } from 'react-apollo'

import {
  ClientPlatform,
  Environment,
  GenericAnalyticsClient,
  HttpHeader,
  inferAuthHeaders,
  withAnalyticsClient,
  withEnv,
} from '@acorns/web-utils'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { datadogRum } from '@datadog/browser-rum'
import { ApolloClient } from 'apollo-client'
import { ApolloLink, Observable, from, split } from 'apollo-link'
import { BatchHttpLink } from 'apollo-link-batch-http'
import DebounceLink from 'apollo-link-debounce'
import { ErrorResponse, onError } from 'apollo-link-error'
import { ServerError } from 'apollo-link-http-common'
import { createPersistedQueryLink } from 'apollo-link-persisted-queries'
import { createUploadLink } from 'apollo-upload-client'
import { createClient } from 'graphql-ws'
import {
  DefinitionNode,
  Kind,
  OperationDefinitionNode,
  VariableDefinitionNode,
} from 'graphql/language'
import { Option, Try } from 'monapt'
import { insert, path, pathOr } from 'ramda'
import { compose, withProps } from 'recompose'
import { UAParser } from 'ua-parser-js'

import { ModalActionTypes } from 'components/ConfirmationModal'
import { trackGraphQLError } from 'utils/analytics'
import {
  OPERATIONS,
  hasServiceUnavailableError,
  hasUnauthenticatedError,
} from 'utils/authentication'
import { buildSignInUrl } from 'utils/authentication/buildSignInUrl'
import { withApolloErrorHandler } from 'utils/error'
import getAppVersion from 'utils/get-app-version'
import {
  SESSION_REFRESH_QUEUED_SESSION_STORAGE_KEY,
  debouncedRefreshSessionCookies,
  logout,
  resolveTokenFromStorage,
} from 'utils/session'

import {
  IErrorDetails,
  IErrorPayload,
  withApolloErrorModal,
} from '../utils/error'
import { createCache } from './apollo-cache'
import { createTimingLink } from './links/create-timing-link'
import stateLink from './links/state'

window.onGetAuthentication = (auth: string) => {
  const authData = JSON.parse(auth)
  window.mobileToken = authData.token
  window.mobileUuid = authData.uuid
  window.mobileSessionId = authData.sessionId
}

type GetCustomHandlerType = (
  name: string,
) => (error: IErrorPayload) => IErrorDetails | boolean

type OuterProps = {
  graphqlUrl: string
}

type ProviderProps = {
  getCustomHandler: GetCustomHandlerType
  env: Environment
  showErrorModal: (details?: IErrorDetails) => void
  closeErrorModal: () => void
  isWebView: boolean
  analytics: GenericAnalyticsClient
  bypassAuthentication: boolean
} & OuterProps

const DEBOUNCE_TIMEOUT = 250

const POLL_MOBILE_TOKEN_DELAY = 20
const POLL_MOBILE_TOKEN_TIMEOUT = 5000

const ALL_TRANSFERABLE_FROM_ERROR_CODES = new Set([
  'docs_required',
  'frozen_funding_source',
  'funding_source_paused',
  'funding_source_pending_rdv',
  'funding_source_unapproved',
  'no_funding_source',
  'paypal_funding_source',
  'rdvs_initiated',
  'rdvs_settled',
  'unverified_funding_source',
])

export const ERROR_CODE_BLACKLIST = new Set([
  ...Array.from(ALL_TRANSFERABLE_FROM_ERROR_CODES),
  'change_forbidden_without_rdv',
  'currentbankstatement_not_provided',
  'funding_source_frozen',
  'invalid_role',
  'not_authorized',
  'query_timeout',
  'spend_account_locked',
  'uncategorized_error',
])

export const parseASTVariable = ({ type }: VariableDefinitionNode): boolean => {
  if (type.kind === Kind.NON_NULL_TYPE) {
    return (type as any).type?.name?.value === 'Upload'
  }
  return false
}

export const parseVariableDefinitions = ({
  operation,
  variableDefinitions = [],
}: OperationDefinitionNode): boolean => {
  if (operation === 'mutation') {
    return variableDefinitions.some(parseASTVariable)
  }
  return false
}

export const operationIncludesUpload = ({
  definitions,
}: {
  definitions: [OperationDefinitionNode]
}): boolean => definitions.some(parseVariableDefinitions)

export const operationIsSubscription = (
  definitions: readonly DefinitionNode[],
) => {
  return definitions.some(
    (definition) =>
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription',
  )
}

// Wrapping ApolloProvider so that apollo-link-error can access React props
export class ApolloProviderWrapper extends React.PureComponent<
  OuterProps & ProviderProps,
  {}
> {
  private redirecting = false
  private apolloClient: ApolloClient<object>
  private build: string
  private userAgentParser: UAParser

  constructor(props: OuterProps & ProviderProps) {
    super(props)

    // NOTE: Ideally, ApolloProviderWrapper should accept an Apollo client via props (i.e. via dependency injection).
    // As-is, we can't unit test ApolloProviderWrapper's internal Apollo client usages because it internally creates its own.
    this.apolloClient = this.createClient()
    this.build = getAppVersion()
    this.userAgentParser = new UAParser()
  }

  public render() {
    return (
      <ApolloProvider client={this.apolloClient}>
        {this.props.children}
      </ApolloProvider>
    )
  }

  protected createClient() {
    // Note: Attaches some HTTP header on every network request
    const headersMiddleware = this.createHeadersMiddleware()

    // Note: All GraphQL and network errors get run through this
    // so we can take appropriate action
    const errorLink = this.createErrorLink()

    // Note: Performs debounce logic on queries that have a `debounceKey` configured
    const debounceLink = new DebounceLink(DEBOUNCE_TIMEOUT) as any

    // Note: Generates and optimistically (in OPTION request)
    // sends query checksums instead of full query text
    const persistedQueriesLink = createPersistedQueryLink()

    const batchConfig =
      this.props.env.get('graphqlBatchingEnabled') === 'true'
        ? {}
        : {
            batchMax: 1,
            batchInterval: 0,
          }

    // Note: Batch queries executed within an interval together
    // in a single network request
    const batchHttpLink = new BatchHttpLink({
      uri: this.props.graphqlUrl,
      credentials: 'include',
      ...batchConfig,
    })

    const sessionLink =
      !this.props.isWebView || !this.props.bypassAuthentication
        ? this.createSessionRefreshLink()
        : null

    const uploadlink = createUploadLink({
      uri: this.props.graphqlUrl,
      credentials: 'include',
    })

    const subscriptionLink = new GraphQLWsLink(
      createClient({
        url: this.props.env.get('graphqlSubscriptionUrl'),
        connectionParams: () => {
          return this.createDefaultHeaders()
        },
      }),
    )

    const splitLink = split(
      ({ query }: any): boolean => operationIncludesUpload(query),
      uploadlink as any,
      split(
        (operation) => operationIsSubscription(operation.query.definitions),
        subscriptionLink as any,
        batchHttpLink,
      ),
    )

    const timingLink = createTimingLink(this.props.analytics)

    let links = [
      headersMiddleware,
      ...(!this.props.isWebView ? [sessionLink] : []),
      errorLink,
      stateLink,
      debounceLink,
      timingLink,
      splitLink,
    ]

    if (process.env.BUILD_ENV === 'production') {
      links = insert(links.indexOf(timingLink), persistedQueriesLink, links)
    }

    if (process.env.BUILD_ENV !== 'production') {
      if (window.localStorage.getItem('apq_disabled') !== 'true') {
        links = insert(links.indexOf(timingLink), persistedQueriesLink, links)
      }
    }

    const client = new ApolloClient({
      cache: createCache(),
      link: from(links) as any,
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'cache-first',
        },
      },
    })

    return client
  }

  protected trackAnalytics(
    operationType: string,
    operationName: string,
    statusCode: number | null,
    error: any,
  ): void {
    trackGraphQLError(
      this.props.analytics,
      operationType,
      operationName,
      statusCode,
      error,
    )
  }

  protected pollMobileToken(
    resolve: (token: string) => void,
    delay: number = POLL_MOBILE_TOKEN_DELAY,
    totalDelay = 0,
  ) {
    if (
      window.mobileToken !== undefined ||
      totalDelay >= POLL_MOBILE_TOKEN_TIMEOUT
    ) {
      resolve(window.mobileToken)
      return
    }

    setTimeout(
      () => this.pollMobileToken(resolve, delay, totalDelay + delay),
      delay,
    )
  }

  protected getMobileToken(client: string): Promise<string | null> {
    return new Promise<string | null>((resolve) => {
      if (window.mobileToken === undefined) {
        const params = new URLSearchParams(window.location.search)
        window.mobileToken = params.get('mobileToken') || undefined
        window.mobileUuid = params.get('mobileUuid') || undefined
        window.mobileSessionId = params.get('mobileSessionId') || undefined
      }

      if (window.mobileToken !== undefined && window.mobileUuid !== undefined) {
        resolve(window.mobileToken)
      } else if (
        client === 'iOS' &&
        window.webkit &&
        window.webkit.messageHandlers &&
        window.webkit.messageHandlers.getAuthentication !== undefined
      ) {
        window.webkit.messageHandlers.getAuthentication.postMessage(
          'send token',
        )
        this.pollMobileToken(resolve)
      } else {
        resolve(null)
      }
    })
  }

  protected safeGetMobileToken(client = ''): Promise<string | null> {
    return Try(() => {
      return this.getMobileToken(client)
    }).getOrElse(() => {
      return Promise.resolve(null)
    })
  }

  protected createHeadersMiddleware() {
    return new ApolloLink((operation, forward) => {
      return new Observable((observer) => {
        const { os } = this.userAgentParser.getResult()

        // Token needed for webviews on Mobile Apps. Only used if present.
        this.safeGetMobileToken(os.name).then((mobileToken) => {
          let customHeaders = this.createDefaultHeaders()

          if (mobileToken !== null) {
            // This `isJwt` logic is temporary until JWT authentication is rolled out completely.
            // We could do something fancier to identify whether the have a JWT, for example:
            // `JSON.parse(atob(mobileToken.split('.')[0])).typ === 'JWT')`
            // ...but checking whether the length of the token passed to us is greater than the
            // length of the legacy auth token (64 characters) is good enough here. The JWT is
            // going to be really long.
            //
            // Note:
            // In case you're wondering where the refresh token is, web-app isn't currently
            // responsible for extending the session within a webview. The assumption is a user
            // in a webview won't be in a webview for very long and the "lifetime" of the JWT will
            // be long enough to cover the time the user spends in the webview.
            // We should also keep in mind there's a max length for query strings.
            //
            // There might come a time when this assumption is incorrect and we'll have a webview where we
            // expect a user will spend a long time in it -- longer than the lifetime of an access token.
            // If/when we need to handle this case in the future, one strategy to handle this will be to
            // send a "message" to the iOS/Android app whenever we identify we have an expired token.
            // The iOS/Android apps will use this message to refresh the JWTs using their own internal
            // mechanisms and will ultimately pass a fresh access token back to the webview to use.
            // This prevents the refresh token from needing to be passed to the webview and prevents any
            // complexity involved in syncing the token pair between the webview and the iOS/Android apps
            // in the scenario the webview (web-app) is responsible for refreshing the access token.
            const isJwt = mobileToken.length > 64

            const authHeaders = isJwt
              ? {
                  [HttpHeader.AuthStrategy]: 'jwt',
                  [HttpHeader.Authorization]: `Bearer ${mobileToken}`,
                }
              : {
                  [HttpHeader.Authorization]: `token ${mobileToken}`,
                }

            customHeaders = Object.assign({}, customHeaders, authHeaders)
          }

          operation.setContext({
            headers: customHeaders,
          })

          return forward(operation).subscribe(observer)
        })
      })
    })
  }

  protected createDefaultHeaders() {
    const { browser, device, os } = this.userAgentParser.getResult()

    const tokenOverride = resolveTokenFromStorage()
    const authHeaders = inferAuthHeaders(tokenOverride)

    return {
      [HttpHeader.ClientAppName]: 'web-app',
      [HttpHeader.ClientBrowserVersion]: browser.version,
      [HttpHeader.ClientBrowserName]: browser.name,
      [HttpHeader.ClientBuild]: this.build,
      [HttpHeader.ClientHardware]: device.model,
      [HttpHeader.ClientOperatingSystem]: os.name,
      [HttpHeader.ClientPlatform]: ClientPlatform.Web,
      ...authHeaders,
    }
  }

  protected createSessionRefreshLink() {
    return new ApolloLink((operation, forward) => {
      // If a session refresh has been queued, debounce it until the GraphQL operations have settled down
      if (
        window.sessionStorage.getItem(
          SESSION_REFRESH_QUEUED_SESSION_STORAGE_KEY,
        ) === 'true' &&
        operation.operationName !== 'RefreshAuthTokens'
      ) {
        debouncedRefreshSessionCookies(this.apolloClient)
      }

      return forward(operation)
    })
  }

  protected createErrorLink() {
    return onError(this.errorLinkHandler.bind(this) as any)
  }

  protected errorLinkHandler({
    graphQLErrors,
    networkError,
    operation,
  }: ErrorResponse) {
    if (this.isRedirecting()) {
      return
    }
    if (operation.operationName === 'RootUserQuery') {
      if (!this.props.bypassAuthentication) {
        this.redirectToLoginApp()
      }
    } else if (
      !OPERATIONS.includes(operation.operationName) &&
      hasUnauthenticatedError(graphQLErrors)
    ) {
      if (this.props.bypassAuthentication) {
        return
      }
      this.handleAuthenticationError()
    } else if (hasServiceUnavailableError(graphQLErrors)) {
      this.handleServiceUnavailableError()
    } else {
      this.handleError(graphQLErrors, networkError, operation)
    }
  }

  protected createNetworkCallback(cb: () => boolean) {
    return new ApolloLink((operation, forward) => {
      if (cb()) {
        return forward(operation)
      }

      return null
    })
  }

  protected isRedirecting() {
    return this.redirecting
  }

  protected redirectToLoginApp() {
    this.redirecting = true
    window.location.href = buildSignInUrl(
      this.props.env.get('loginUrl'),
      window.location.href,
    )
  }

  protected async handleAuthenticationError() {
    const errorModalHandler = () => {
      this.redirectToLoginApp()
    }

    // it's possible we're here because we attempted to use an invalid/expired auth token;
    // delete any auth tokens on the client if that's the case
    await logout(this.apolloClient)

    this.props.showErrorModal({
      header: 'Logged Out',
      body: "You've been logged out due to inactivity. Please log in to proceed.",
      actions: [
        {
          handler: errorModalHandler,
          label: 'Ok',
          type: ModalActionTypes.primary,
        },
      ],
    })

    setTimeout(errorModalHandler, 2000)
  }

  protected handleServiceUnavailableError() {
    this.props.showErrorModal({
      header: 'Something went wrong',
      body: 'We are sorry, an error has occurred. Our technical team is looking into this. Please try again.',
      actions: [
        {
          handler: this.props.closeErrorModal,
          label: 'Ok',
          type: ModalActionTypes.primary,
        },
      ],
    })
  }

  protected reportError(
    graphQLErrors: ErrorResponse['graphQLErrors'],
    networkError: ErrorResponse['networkError'],
    operation: ErrorResponse['operation'],
    operationType: string,
  ) {
    const graphQLErrorCode = path<string>(
      ['0', 'code'],
      graphQLErrors,
    ) as string
    const networkStatusCode = path<number>(
      ['0', 'data', 'status'],
      graphQLErrors,
    ) as number
    const name = Option(operation)
      .flatMap(({ operationName }) => Option(operationName))
      .getOrElse(() => 'AnonymousOperation')

    // do not proceed if networkStatusCode is not a number
    if (networkStatusCode && !Number.isInteger(networkStatusCode)) {
      return
    }

    // Don't report expected errors
    if (ERROR_CODE_BLACKLIST.has(graphQLErrorCode)) {
      return
    }

    if (name === 'RoundUpAccount' && graphQLErrorCode === 'not_found') {
      return
    }

    const statusCode: number | null =
      (networkError as ServerError)?.statusCode ?? null

    // Don't report when fetch has been cancelled by client
    if (graphQLErrors || statusCode) {
      // Send errors to Segment
      if (graphQLErrors && graphQLErrors.length > 0) {
        graphQLErrors.forEach((graphQLError) => {
          this.trackAnalytics(operationType, name, statusCode, graphQLError)
        })
      } else {
        // accommodates a network error that isn't a graphql error, just in case that's possible
        this.trackAnalytics(operationType, name, statusCode, networkError)
      }

      const isClientError = networkStatusCode >= 400 && networkStatusCode < 500

      if (!isClientError && graphQLErrors && graphQLErrors.length > 0) {
        graphQLErrors.forEach((error) => {
          datadogRum.addError(new Error(`GraphQl Error ${error.path}`), {
            context: error,
          })
        })
      }
    }
  }

  protected handleError(
    graphQLErrors: ErrorResponse['graphQLErrors'],
    networkError: ErrorResponse['networkError'],
    operation: ErrorResponse['operation'],
  ) {
    const operationType = pathOr<string>(
      'operation',
      ['query', 'definitions', '0', 'operation'],
      operation,
    )

    const handler = this.props.getCustomHandler(operation.operationName)

    let errorDetails

    if (typeof handler === 'function') {
      errorDetails = handler({
        graphQLErrors,
        networkError,
      })
    }

    Promise.resolve(errorDetails).then((results) => {
      this.reportError(graphQLErrors, networkError, operation, operationType)

      // Disable error modal if handler returns false
      if (typeof results === 'boolean' && !results) {
        return
      }

      // Disable error modal if logging out
      if (operation.operationName === 'Logout') {
        return
      }

      if (operationType === 'mutation' || results) {
        this.props.showErrorModal(results as IErrorDetails)
      }
    })
  }
}

const enhance = compose<ProviderProps, OuterProps>(
  withProps(() => ({
    isWebView: window.location.pathname.startsWith('/wv/'),
    bypassAuthentication:
      (window.location.pathname + window.location.search).match(
        /^\/document-uploader-suspension\?token=*/,
      ) ||
      window.location.pathname.match(/^\/recap\/beneficiary\/[a-z0-9]/) ||
      window.location.pathname.match(/^\/recap\/article\/[a-z0-9]/) ||
      window.location.pathname.match('/terms'),
  })),
  withAnalyticsClient,
  withApolloErrorModal,
  withEnv,
  withApolloErrorHandler('RefreshCookie', () => () => false),
)

export const Provider = enhance(ApolloProviderWrapper)
